import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '@/assets/app/css/faq.module.css';
import TitleHeader from '../common/TitleHeader';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import useAppAuthStore from '@/shared/store/UseAppAuthStore';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const { member } = useAppAuthStore();
  const [openId, setOpenId] = useState<number | null>(null); // 아코디언 상태
  const [searchQuery, setSearchQuery] = useState('');

  const faqs: FAQItem[] = [
    {
      id: 1,
      question: '나의 기록을 보고 싶어요',
      answer: '마이페이지에서 나의 학습 기록과 업적을 확인할 수 있습니다.',
    },
    {
      id: 2,
      question: '문제풀이를 다시금 싶어요',
      answer: '퀴즈 히스토리 페이지에서 이전에 풀었던 문제를 다시 확인하고 풀 수 있습니다.',
    },
    {
      id: 3,
      question: '비밀번호를 분실했어요',
      answer:
        '로그인 화면에서 "비밀번호 찾기"를 클릭하여 등록된 이메일로 비밀번호 재설정 링크를 받으실 수 있습니다. 카카오/네이버 등 소셜 로그인을 사용하시는 경우 해당 플랫폼에서 비밀번호를 재설정하실 수 있습니다.',
    },
    {
      id: 4,
      question: '별 포인트 내역을 확인하고 싶어요',
      answer: '마이페이지의 "별 포인트 내역" 메뉴에서 획득 및 사용 내역을 확인할 수 있습니다.',
    },
    {
      id: 5,
      question: '개인정보를 확인하고 싶어요',
      answer: '설정 > 내정보에서 개인정보를 확인하고 수정할 수 있습니다.',
    },
    {
      id: 6,
      question: '비밀번호를 잊어버렸어요',
      answer: '로그인 화면의 "비밀번호 찾기"를 통해 재설정할 수 있습니다.',
    },
    {
      id: 7,
      question: '별 포인트를 구매할 수 있나요?',
      answer: '현재는 별 포인트 구매 기능을 제공하지 않으며, 퀴즈를 풀어서만 획득할 수 있습니다.',
    },
    {
      id: 8,
      question: '문제 추천을 받고 싶어요',
      answer: 'AI가 나의 학습 수준에 맞는 문제를 자동으로 추천해드립니다.',
    },
  ];

  // 🔍 검색 로직: 질문(question)에 검색어가 포함된 항목만 필터링
  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const toggleAccordion = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  const handleToggle = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <TitleHeader title="자주하는 질문" onBack={() => navigate(-1)} />

      <main className="p-5 bg-[#F0F7FF] mx-5 rounded-[20px] mt-2" style={{ height: 91 }}>
        <h2
          className="text-lg font-medium mb-6 leading-tight"
          style={{ fontSize: 16, lineHeight: '26px', fontWeight: 800, fontFamily: 'SUIT' }}
        >
          안녕하세요. <span className="text-[#0094EF]">{member?.nickname}</span>님<br />
          무엇을 도와드릴까요?
        </h2>
      </main>
      <main className="p-5">
        {/* 검색바 */}
        <div className="relative mb-6">
          <input
            type="text"
            placeholder="자주하는 질문 검색"
            className="w-full border-2 border-[#d7d7d7] rounded-xl p-4 focus:outline-none focus:border-cyan-400"
            style={{ border: '1px solid #d7d7d7', fontSize: 18, borderRadius: 40 }}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* 아코디언 리스트 */}
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq, index) => (
              <div key={faq.id}>
                <button
                  onClick={() => toggleAccordion(faq.id)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:border-[#F2F2F2] transition-colors"
                >
                  <span className={`text-sm font-medium text-color-[#4B4B4B]`}>{faq.question}</span>
                  {openId === faq.id ? (
                    <ChevronUp className="w-5 h-5 text-color-[#4B4B4B]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-color-[#4B4B4B]" />
                  )}
                </button>

                {openId === faq.id && (
                  <div className="px-5 pb-5 text-xs leading-relaxed text-color-[#7F7F7F] border-[#F2F2F2]">
                    <div className="pt-2 border-t border-gray-100 mt-0">{faq.answer}</div>
                  </div>
                )}

                {index !== filteredFaqs.length - 1 && <hr className="mx-4 border-[#F2F2F2]" />}
              </div>
            ))
          ) : (
            <div className="p-10 text-center text-gray-400 text-sm">검색 결과가 없습니다.</div>
          )}
        </section>
      </main>
    </div>
  );
};

export default FAQ;
